// Name property per UI language in the CARTO (OpenMapTiles) vector tiles.
// Only en/de have the underscore variants; the other languages use "name:<lang>".
const NAME_FIELDS: Record<string, string> = {
    en: "name_en",
    de: "name_de",
    fr: "name:fr",
    ko: "name:ko",
    zh: "name:zh",
};

// BCP 47 tags for Intl.DisplayNames (Chinese UI = Simplified Chinese).
const INTL_LOCALES: Record<string, string> = { zh: "zh-CN" };

// The tiles lack some continent names (e.g. name:ko for Europe, Africa and North
// America) and mix scripts in others ("欧洲/歐洲"), so continents use a fixed list,
// keyed by name_en. English keeps the tile data.
const CONTINENTS: Record<string, Record<string, string>> = {
    de: { "Africa": "Afrika", "Antarctica": "Antarktika", "Asia": "Asien", "Europe": "Europa", "North America": "Nordamerika", "South America": "Südamerika", "Oceania": "Ozeanien", "Australia": "Australien", "America": "Amerika" },
    fr: { "Africa": "Afrique", "Antarctica": "Antarctique", "Asia": "Asie", "Europe": "Europe", "North America": "Amérique du Nord", "South America": "Amérique du Sud", "Oceania": "Océanie", "Australia": "Australie", "America": "Amérique" },
    ko: { "Africa": "아프리카", "Antarctica": "남극", "Asia": "아시아", "Europe": "유럽", "North America": "북아메리카", "South America": "남아메리카", "Oceania": "오세아니아", "Australia": "오스트레일리아", "America": "아메리카" },
    zh: { "Africa": "非洲", "Antarctica": "南极洲", "Asia": "亚洲", "Europe": "欧洲", "North America": "北美洲", "South America": "南美洲", "Oceania": "大洋洲", "Australia": "澳大利亚", "America": "美洲" },
};

const baseLanguage = (language?: string): string => {
    const lng = (language ?? "").toLowerCase().split("-")[0];
    return lng in NAME_FIELDS ? lng : "en";
};

export const getMapNameField = (language?: string): string => NAME_FIELDS[baseLanguage(language)];

/**
 * name:zh in the tiles often holds Simplified and Traditional Chinese together
 * ("德国/德國", "亚洲;亞洲"). Show only the part before the first "/" or ";".
 */
const firstNameSegment = (field: string, fallback: unknown[]): unknown[] => [
    "case",
    ["has", field],
    ["let", "n", ["get", field],
        ["let", "i",
            ["min",
                ["let", "a", ["index-of", "/", ["var", "n"]], ["case", ["<", ["var", "a"], 0], 1000, ["var", "a"]]],
                ["let", "b", ["index-of", ";", ["var", "n"]], ["case", ["<", ["var", "b"], 0], 1000, ["var", "b"]]]],
            ["case", [">=", ["var", "i"], 1000], ["var", "n"], ["slice", ["var", "n"], 0, ["var", "i"]]]]],
    fallback,
];

const placeName = (lang: string): unknown[] => {
    // Inside a "match" every branch must yield a string, so no null placeholders.
    const fallback = ["coalesce", ["get", "name_en"], ["get", "name"]];
    return lang === "zh"
        ? firstNameSegment(NAME_FIELDS.zh, fallback)
        : ["coalesce", ["get", NAME_FIELDS[lang]], ...fallback.slice(1)];
};

/** Country names from the browser's CLDR data, keyed by the tiles' iso_a2. */
const countryNames = (lang: string): [string, string][] => {
    const names = new Intl.DisplayNames([INTL_LOCALES[lang] ?? lang], { type: "region", fallback: "none" });
    const result: [string, string][] = [];
    for (let a = 65; a <= 90; a++) {
        for (let b = 65; b <= 90; b++) {
            const code = String.fromCharCode(a, b);
            const name = names.of(code);
            if (name && name !== code) result.push([code, name]);
        }
    }
    return result;
};

const matchExpression = (input: unknown[], pairs: [string, string][], fallback: unknown[]): unknown[] =>
    pairs.length ? ["match", input, ...pairs.flat(), fallback] : fallback;

const NAME_TOKENS = ["{name_en}", "{name}"];

const filterHasClass = (layer: any, cls: string): boolean =>
    JSON.stringify(layer.filter ?? null).includes(`["==","class","${cls}"]`);

/**
 * Make every place label of the CARTO Positron style resolve to one language.
 * The upstream style labels with `{name_en}` (land) and `{name}` (ocean/sea,
 * all languages concatenated), partly in zoom stop functions. Each such
 * text-field becomes: name in the UI language, else English, else the local name.
 * Countries come from Intl.DisplayNames via iso_a2, continents from a fixed list,
 * because the tile data is incomplete for both.
 */
export function applyLanguage(style: any, language: string): any {
    const lang = baseLanguage(language);
    const label = placeName(lang);
    const countryLabel = matchExpression(["get", "iso_a2"], countryNames(lang), label);
    const continentLabel = matchExpression(["get", "name_en"], Object.entries(CONTINENTS[lang] ?? {}), label);

    const isNameField = (field: unknown): boolean => {
        if (typeof field === "string") return NAME_TOKENS.includes(field);
        const stops = (field as any)?.stops;
        return Array.isArray(stops) && stops.every(([, value]: [number, unknown]) => isNameField(value));
    };
    const labelFor = (layer: any): unknown[] => {
        if (filterHasClass(layer, "country")) return countryLabel;
        if (filterHasClass(layer, "continent")) return continentLabel;
        return label;
    };
    return {
        ...style,
        layers: style.layers.map((layer: any) =>
            isNameField(layer.layout?.["text-field"])
                ? { ...layer, layout: { ...layer.layout, "text-field": labelFor(layer) } }
                : layer
        ),
    };
}
